import { AppLayout } from '@/shared/components/layout/app-layout';
import { useStoreModel } from './model';
import { Navbar } from '@/shared/components/layout/navbar';
import { StoreLineInfo } from '@/modules/stores/components/store-line-info';
import { StoreDetailsModal } from '@/modules/stores/components/store-details-modal';
import { SearchIcon } from 'lucide-react';
import type { StoreItem } from '@/modules/stores/types/get-store-items';
import { StoreItemGridCard } from '@/modules/stores/components/store-item-grid-card';
import { CategoryTag } from '@/modules/stores/components/category-tag';
import { Separator } from '@/shared/components/ui/separator';
import { StoreSkeleton, StoreErrorSkeleton } from './skeleton';
import { isStoreNotFound } from './store-error';
import { CartSummary } from '@/modules/cart/components/ui/cart-summary';
import { BottomActionBar } from '@/shared/components/layout/bottom-action-bar';
import { ScrollSpy } from '@/modules/stores/components/scrollspy';
import { cn } from '@/shared/lib/utils';
import { getCategoryId } from '@/shared/utils/category';
import { useState } from 'react';
import { getImageUrl } from '@/shared/utils/image';
import { getNextOpeningTime } from '@/shared/utils/schedule';
import { ClosedStoreCard } from '@/modules/stores/components/closed-store-card';
import { StoreSchedulingModal } from '@/modules/stores/components/store-scheduling-modal';
import { PendingOrderCard } from '@/shared/components/ui/pending-order-card';
import { PendingRatingBanner } from '@/modules/orders/components/pending-rating-banner';

import { Footer } from '@/shared/components/layout/footer';
import { AddressNotServedAlert } from '@/modules/orders/components/address-not-served-alert';
import { ProductDrawer } from '@/modules/stores/components/product-drawer';
import { ShowcaseBanner } from '@/modules/stores/components/showcase-notice';
import { isShowcaseOnly } from '@/modules/stores/components/showcase';
import { PromotionsModal } from '@/modules/stores/components/promotions-modal';
import { StoreReviewsDrawer } from '@/modules/stores/components/store-reviews-drawer';
import { StoreBenefitsStrip } from '@/modules/stores/components/store-benefits-strip';
import { StorePushPrompt } from '@/modules/push/components/store-push-prompt';
import { ProfileNudgePill } from '@/modules/profile-completion/components/profile-nudge-pill';
import { useLocation } from 'wouter';
import { StoreCouponsDrawer } from '@/modules/stores/components/store-coupons-drawer';
import { StoreBenefitsBar } from '@/modules/stores/components/store-benefits-bar';
import { StoreHighlights } from '@/modules/stores/components/store-highlights';
import { StoreCover } from '@/modules/stores/components/store-cover';
import { QuantityPromotionChip } from '@/modules/stores/components/quantity-promotion-chip';

export function StoreView(props: ReturnType<typeof useStoreModel>) {
  const {
    quantityPromotion,
    wholesaleFromFor,
    handleBack,
    handleSelectProduct,
    storeInfoQuery,
    storeItemsQuery,
    hasCartItems,
    isStoreDetailsModalOpen,
    handleOpenStoreDetails,
    handleCloseStoreDetails,
    isReviewsOpen,
    handleOpenReviews,
    handleCloseReviews,
    storeStatus,
    isSchedulingInfoModalOpen,
    handleCloseSchedulingInfo,
    referralProgram,
    handleOpenReferral,
    searchTerm,
    handleSearchChange,
    trackCategoryView,
    isProductDrawerOpen,
    handleCloseProductDrawer,
    selectedProductId,
    slug,
    promotionalItems,
    isPromotionsModalOpen,
    handleClosePromotionsModal,
    coupons,
    clubSummary,
    couponProgress,
    selectedCouponCode,
    isCouponsDrawerOpen,
    handleOpenCoupons,
    handleCloseCoupons,
    handleSelectCoupon,
    handleOpenClub,
    highlightItems,
  } = props;

  // PRAT-161: o botão de completar o perfil no topo abre o Meu perfil.
  const [, navigateTo] = useLocation();

  const [activeCategory, setActiveCategory] = useState<string>('');

  const isLoading = storeInfoQuery.isLoading || storeItemsQuery.isLoading;
  const hasData = storeInfoQuery.data && storeItemsQuery.data;
  // `isStoreOpen` controla o visual de "loja fechada" (grayscale, card).
  // `canBrowse` libera a navegação/montagem do pedido — verdadeiro quando a
  // loja está aberta OU fechada mas aceitando agendamento.
  // PRATDEV-260: loja só vitrine — o cardápio aparece sempre (aberta ou
  // fechada, não há pedido); sem sacola nem cupom, com a faixa fixa no lugar.
  const showcaseOnly = isShowcaseOnly(storeInfoQuery.data);
  const isStoreOpen = showcaseOnly || storeStatus.isOpen;
  const canBrowse = showcaseOnly || storeStatus.canBrowse;

  // Barra do rodapé (PRAT-110): cupom com progresso e/ou resumo do carrinho.
  const showCouponBar = !showcaseOnly && canBrowse && !!couponProgress;
  const showCart = !showcaseOnly && hasCartItems;
  const hasBottomBar = showcaseOnly || showCart || showCouponBar;

  const categories = props.categories || [];
  const filteredCategories = categories.map(category => category.name);
  const hasSearchResults = categories.length > 0;

  // Mostrar skeleton enquanto carrega OU se não tem dados ainda
  if (isLoading || !hasData) {
    // Se já tentou carregar e deu erro, mostrar erro
    if (!isLoading && (storeInfoQuery.isError || storeItemsQuery.isError)) {
      return (
        <StoreErrorSkeleton
          onBack={handleBack}
          notFound={isStoreNotFound(storeInfoQuery.error, storeItemsQuery.error)}
        />
      );
    }
    // Caso contrário, mostrar skeleton
    return <StoreSkeleton onBack={handleBack} />;
  }

  return (
    <div className="animate-in fade-in duration-300">
      <AppLayout hideBottomTabShadow={hasBottomBar}>
        {/** Header */}
        <Navbar onBack={handleBack}>
          <div className="flex-1 flex items-center justify-start ml-12 z-10">
            <img src="/logo.svg" alt="Logo" className="h-8 w-auto max-w-32 object-contain" />
          </div>
          {/* PRAT-161: chamada discreta para completar o perfil (some quando completo). */}
          <div className="z-10 flex shrink-0 items-center">
            <ProfileNudgePill slug={slug} onOpen={() => navigateTo('/profile')} />
          </div>
        </Navbar>

        {/** Content */}
        <div className="flex-1 w-full flex flex-col items-center justify-start">
          <StoreCover
            coverUrl={
              // Capa do gestor é um id do Cloudflare; o legado "assets/img/nocover.jpg" não é capa.
              storeInfoQuery.data?.cover && !storeInfoQuery.data.cover.includes('/')
                ? getImageUrl(storeInfoQuery.data.cover)
                : null
            }
            grayscale={!isStoreOpen}
          />
          <div className="bg-white rounded-t-2xl translate-y-4 w-full flex-1 -mt-10 z-12 px-4 sm:px-5 pt-5 pb-8 sm:py-6 space-y-4 sm:space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-300">
            {/** Container que compõe as informações da loja e logo abaixo o input de pesquisa */}
            <div className="flex flex-col justify-center space-y-3">
              {/** Informações da loja */}
              <div className="flex gap-3">
                <div
                  className="rounded-xl overflow-hidden bg-gray-100 flex-shrink-0"
                  style={{ width: '72px', height: '72px', minWidth: '72px', minHeight: '72px' }}
                >
                  <img
                    src={getImageUrl(storeInfoQuery.data?.image)}
                    alt="Logo"
                    className={cn(
                      'w-full h-full object-contain bg-white',
                      !isStoreOpen && 'grayscale'
                    )}
                    style={{ width: '100%', height: '100%', display: 'block' }}
                    onError={e => {
                      e.currentTarget.src = '/fallback.svg';
                    }}
                  />
                </div>
                <div className="flex flex-col items-start justify-center gap-1">
                  <p className="text-sm font-medium">{storeInfoQuery.data?.name}</p>
                  <div className="flex flex-col items-start justify-center">
                    <p className="text-xs text-gray-500">
                      {storeInfoQuery.data?.location?.address
                        ? `${storeInfoQuery.data.location.address.street}, ${storeInfoQuery.data.location.address.city}`
                        : 'Endereço não disponível'}
                    </p>
                    <button
                      className="text-xs text-primary hover:text-primary/80 transition-colors"
                      onClick={handleOpenStoreDetails}
                    >
                      Ver mais
                    </button>
                  </div>
                  <StoreLineInfo
                    store={{
                      rating:
                        storeInfoQuery.data?.reviews_enabled === false
                          ? undefined
                          : storeInfoQuery.data?.rating || '--',
                      delivery_time: storeInfoQuery.data?.delivery_time
                        ? `${storeInfoQuery.data.delivery_time.min}-${storeInfoQuery.data.delivery_time.max}`
                        : '--',
                      min_order_price: storeInfoQuery.data?.min_order_price || '--',
                    }}
                    ratingCount={storeInfoQuery.data?.rating_count}
                    // PRAT-44: loja que desligou as avaliações não abre a lista.
                    onRatingClick={
                      storeInfoQuery.data?.reviews_enabled === false ? undefined : handleOpenReviews
                    }
                  />
                  {/* Tempo de retirada em destaque no bloco de dados da loja,
                    quando a loja aceita retirada (delivery_type 2 ou 3) e
                    informa o tempo estimado (issue 229). */}
                  {(storeInfoQuery.data?.delivery_type === 2 ||
                    storeInfoQuery.data?.delivery_type === 3) &&
                    storeInfoQuery.data?.preparation_time != null && (
                      <p className="text-xs font-semibold text-primary">
                        Retire na loja em apenas {storeInfoQuery.data.preparation_time} minutos
                      </p>
                    )}
                </div>
              </div>

              {/** Input de pesquisa customizado - disponível quando é possível navegar */}
              {canBrowse && (
                <div className="flex items-center w-full rounded-2xl border border-gray-400 py-3.5 px-5 bg-white">
                  <input
                    type="text"
                    placeholder="Pesquisar"
                    value={searchTerm}
                    onChange={e => handleSearchChange(e.target.value)}
                    className="flex-1 border-none bg-none outline-none shadow-none bg-transparent text-gray-700 placeholder:text-gray-400 text-xs placeholder:text-xs"
                  />
                  <SearchIcon className="w-3.5 h-3.5 text-gray-400 ml-3" />
                </div>
              )}

              {/** Card de Pedido em Andamento nesta loja */}
              <PendingOrderCard merchantSlug={storeInfoQuery.data?.slug} />

              {/** PRAT-111: avalie o último pedido desta loja com 1 toque */}
              {storeInfoQuery.data?.slug && (
                <PendingRatingBanner merchantSlug={storeInfoQuery.data.slug} />
              )}

              {/** Benefícios da loja (PRAT-110): cupons, Clube e Afili'amados em chips.
                Some sozinho quando a loja não tem nenhum ativo. */}
              <StoreBenefitsStrip
                coupons={coupons}
                club={clubSummary}
                referral={referralProgram}
                onOpenCoupons={handleOpenCoupons}
                onOpenClub={handleOpenClub}
                onOpenReferral={handleOpenReferral}
              />

              {/** PRAT-165/PRAT-43: ofertas para quem volta à loja / iPhone na tela de início + brinde. */}
              {storeInfoQuery.data?.slug && (
                <StorePushPrompt
                  slug={storeInfoQuery.data.slug}
                  storeName={storeInfoQuery.data.name}
                  merchantId={storeInfoQuery.data.id}
                />
              )}

              {/** Alerta de endereço não atendido */}
              {canBrowse && storeInfoQuery.data?.delivers_to_address === false && (
                <AddressNotServedAlert />
              )}
            </div>
            {/** Closed Store Card - positioned right after store info */}
            {!isStoreOpen && storeInfoQuery.data?.schedule_data && (
              <ClosedStoreCard
                scheduleData={storeInfoQuery.data.schedule_data}
                isSchedulable={storeStatus.isClosedButSchedulable}
              />
            )}
          </div>

          {/** ScrollSpy das categorias - sticky */}
          {canBrowse && filteredCategories.length > 0 && (
            <ScrollSpy
              categories={filteredCategories}
              activeCategory={activeCategory}
              onSetActive={category => {
                setActiveCategory(category);
                trackCategoryView?.(category);
              }}
              className="w-full animate-in fade-in duration-300 delay-100"
            />
          )}

          <div className="bg-white w-full flex-1 px-4 sm:px-5 py-4 sm:py-6 space-y-4 sm:space-y-6 animate-in fade-in duration-300 delay-150">
            {/** Conteúdo disponível quando é possível navegar (aberta ou agendável) */}
            {canBrowse && (
              <>
                {/** Mensagem quando não há resultados de busca */}
                {searchTerm && !hasSearchResults && (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <SearchIcon className="w-12 h-12 text-gray-400 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      Nenhum resultado encontrado
                    </h3>
                    <p className="text-gray-500 text-sm">
                      Não encontramos produtos para "{searchTerm}". Tente buscar por outro termo.
                    </p>
                  </div>
                )}

                {/** Destaques (PRAT-110): mais pedidos e maiores descontos */}
                {!searchTerm && (
                  <StoreHighlights
                    items={highlightItems}
                    onItemClick={handleSelectProduct}
                    quantityPromotionLabelFor={quantityPromotion.itemLabelFor}
                    wholesaleFromFor={wholesaleFromFor}
                  />
                )}

                {/** Lista de itens */}
                {hasSearchResults && (
                  <div className="flex flex-col w-full">
                    {categories.map(category => (
                      <div
                        key={category.name}
                        id={getCategoryId(category.name)}
                        className="flex flex-col mb-4"
                      >
                        <div className="flex flex-col gap-2">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h2 className="text-base sm:text-lg font-bold font-sans text-night">
                              {category.name}
                            </h2>
                            {/* Etiqueta das categorias virtuais (com fallback de rótulo) */}
                            {category.tag && <CategoryTag tag={category.tag} />}
                            {/* PRAT-184: a categoria participa da promoção por quantidade */}
                            {quantityPromotion.categoryLabelFor(category) && (
                              <QuantityPromotionChip
                                label={quantityPromotion.categoryLabelFor(category)!}
                              />
                            )}
                          </div>
                          <Separator />
                        </div>

                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4 py-4">
                          {category.items.map((item: StoreItem) => (
                            <StoreItemGridCard
                              key={item.id}
                              item={item}
                              onItemClick={handleSelectProduct}
                              quantityPromotionLabel={quantityPromotion.itemLabelFor(item)}
                              wholesaleFrom={wholesaleFromFor(item)}
                            />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {/* Footer */}
            <Footer />
          </div>
        </div>
        {/* Ver sacola e cupom: fixos no rodapé, acima das abas (PRAT-154) */}
        {hasBottomBar && (
          <BottomActionBar>
            {showCouponBar && (
              <StoreBenefitsBar
                progress={couponProgress!}
                total={coupons.length}
                isSelected={
                  !!selectedCouponCode &&
                  selectedCouponCode.toUpperCase() === couponProgress!.coupon.code.toUpperCase()
                }
                onClick={handleOpenCoupons}
              />
            )}
            {showcaseOnly && <ShowcaseBanner />}
            {showCart && (
              <CartSummary
                storeLogo={
                  storeInfoQuery.data?.image ? getImageUrl(storeInfoQuery.data.image) : null
                }
              />
            )}
          </BottomActionBar>
        )}

        {/* Cupons da loja (PRAT-110) */}
        <StoreCouponsDrawer
          isOpen={isCouponsDrawerOpen}
          onClose={handleCloseCoupons}
          coupons={coupons}
          selectedCode={selectedCouponCode}
          onSelect={handleSelectCoupon}
        />

        {/* Avaliações da loja (PRAT-43) */}
        <StoreReviewsDrawer
          isOpen={isReviewsOpen}
          onClose={handleCloseReviews}
          slug={slug}
          merchantName={storeInfoQuery.data?.name}
        />

        {/* Modal de detalhes da loja */}
        <StoreDetailsModal
          isOpen={isStoreDetailsModalOpen}
          onClose={handleCloseStoreDetails}
          store={storeInfoQuery.data}
        />

        {/* Modal informativo (uma vez por sessão): loja fechada mas agendável */}
        <StoreSchedulingModal
          isOpen={isSchedulingInfoModalOpen && !showcaseOnly}
          onClose={handleCloseSchedulingInfo}
          storeName={storeInfoQuery.data?.name}
          nextOpeningTime={
            storeInfoQuery.data?.schedule_data
              ? getNextOpeningTime(storeInfoQuery.data.schedule_data)
              : undefined
          }
        />

        {/* Drawer do produto */}
        <ProductDrawer
          isOpen={isProductDrawerOpen}
          onClose={handleCloseProductDrawer}
          productId={selectedProductId}
          slug={slug!}
          showcaseOnly={showcaseOnly}
        />

        {/* Modal de Promoções (uma vez por sessão): ao selecionar uma oferta,
          reutiliza o mesmo fluxo de detalhe/adicionar ao carrinho do cardápio
          abrindo o ProductDrawer via handleSelectProduct. */}
        <PromotionsModal
          isOpen={isPromotionsModalOpen}
          onClose={handleClosePromotionsModal}
          items={promotionalItems}
          onSelectItem={handleSelectProduct}
        />
      </AppLayout>
    </div>
  );
}
